import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import { DEL_Attribute } from "apollo/mutuations";
import { GET_Attributes } from "apollo/queries";
import AttributeAddModal from "components/Attribute/attributeAddModal";
import AttributeDetailModal from "components/Attribute/attributeDetail";
import AttributeEditModal from "components/Attribute/attributeEditModal";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { Trash } from "tabler-icons-react";

const Attributes = () => {
  const [size, setSize] = useState("10"); // Default page size
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  //
  const [opened, setOpened] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedDetail, setOpenedDetail] = useState(false);

  const theme = useMantineTheme();
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };
  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };
  const { data, loading, fetchMore, refetch } = useQuery(GET_Attributes, {
    // fetchPolicy: "no-cache",
    variables: {
      first: parseInt(size), // Pass size dynamically
      page: activePage,
      search: searchValue,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
  });

  const [delAttribute] = useMutation(DEL_Attribute, {
    update(cache, { data: { deleteAttribute } }) {
      cache.updateQuery(
        {
          query: GET_Attributes,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: "",
            ordered_by: [
              {
                column: "CREATED_AT",
                order: "DESC",
              },
            ],
          },
        },
        (data) => {
          if (data.attributes.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              attributes: {
                data: [
                  ...data.attributes.data.filter(
                    (category) => category.id !== deleteAttribute.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const deleteAttribute = () => {
    delAttribute({
      variables: { id: deleteID },
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Attribute Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to delete attribute ",
        });
      },
    });
  };

  const handleEditCategory = (id) => {
    setEditId(id);
    setOpenedEdit(true);
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.attributes.paginatorInfo.lastPage);
    }
  }, [data, size]);
  useEffect(() => {
    refetch();
  }, []);
  const handleChange = (currentPage) => {
    fetchMore({
      variables: {
        first: parseInt(size),
        page: currentPage,
      },
    });
    setActivePage(currentPage);
  };
  if (!hasMounted) {
    return null;
  }
  const handleManageCategory = (id) => {
    setEditId(id);
    setOpenedDetail(true);
  };
  const headerData = [
    {
      label: "Name",
      key: "name",
      sortable: true,
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.name}</span>;
      },
    },
    {
      label: "Category",
      key: "category",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.category.name}</span>;
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="Edit"
              onClick={() => handleEditCategory(rowData)}
            >
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              color="attention"
              label="Add Values"
              onClick={() => handleManageCategory(`${rowData.id}`)}
            >
              <FiPlus fontSize="medium" />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={17} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: "Add Attribute",
    setAddModal: setOpened,
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpenedEdit(false)}
        position="right"
        size="40%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        <AttributeEditModal setOpenedEdit={setOpenedEdit} attribute={editId} />
      </Drawer>
      <Drawer
        opened={openedDetail}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        title="Attribute Values"
        padding="xl"
        onClose={() => setOpenedDetail(false)}
        position="bottom"
        size="80%"
      >
        <AttributeDetailModal id={editId} />
      </Drawer>

      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure do you want todelete this attribute?</p>
        <Group position="right">
          <Button onClick={() => deleteAttribute()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        padding="xl"
        position="right"
        size="40%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
      >
        <AttributeAddModal
          total={total}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
          setTotal={setTotal}
        />
      </Drawer>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            clearInput={clearInput}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            handleChange={handleChange}
            header={headerData}
            optionsData={optionsData}
            loading={loading}
            data={data ? data.attributes.data : []}
            handlePageSizeChange={handlePageSizeChange}
            size={size}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default Attributes;
